import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import ReactToPrint from 'react-to-print'
import {
  i18nTranslate,
  getLiveEventStatus,
  trackGTMEvents,
  trackCancelOrderEvent,
  convertToBoolean,
  getGPSConfig,
  getWebAvailableChannel,
  isFeatureEnabled,
} from 'src/utils'
import { Row, Col, Container, Button, Dropdown } from 'react-bootstrap'
import isFunction from 'lodash/isFunction'
import { enableReorder, APPConfig } from 'config/appConfig'
import { application, displayOrderReturnForSupportedLocales } from 'src/utils'
import { orderHistoryContainer } from 'src/models'
import { IoIosArrowDown as ArrowDownIcon } from 'react-icons/io'
import {
  Summary,
  CommonForm,
  PrintComponent,
  QRcodeComponent,
} from 'src/views/components'
import { getPrintOrderDetails } from './transformPrintOrder'
import { actionState } from './fixture'
import { cancellationFormInput } from './cancelFormInput'
import { schema } from './validationSchema'
import './styles.scss'
import { toastState } from 'src/views/components'
import { overlayState } from 'src/views/components/Overlay'

function defaultRenderOrderDetails(props) {
  const {
    label = '',
    className = '',
    component = null,
    dataTestId = '',
  } = props
  return (
    <Col
      className={`order-summary-details px-0 pb-3 ${className}`}
      key={`ordersummary-${label}`}
      data-testid={dataTestId}>
      <div>
        <h6> {label} </h6>
        {component}
      </div>
    </Col>
  )
}

@observer
class OrderSummary extends React.Component {
  @observable isCancelClicked = false
  enableLiveEvent = convertToBoolean(getLiveEventStatus())

  constructor(props) {
    super(props)
  }

  getCardInfo = (cardInfo = {}, otherProperties = {}) => {
    const hiddenCardNumber =
      otherProperties?.cardNumberLastFour || otherProperties?.cardNumber || ''
    const provider = cardInfo?.provider || otherProperties?.cardType || ''
    const cardNumber = cardInfo?.cardNumber || hiddenCardNumber || ''
    const slicedCardNumber = cardNumber
      ? cardNumber?.substring(cardNumber?.length - 4) || ''
      : ''

    if (provider && slicedCardNumber) {
      return `${provider} ****${slicedCardNumber}`
    } else if (slicedCardNumber) {
      return ` ****${slicedCardNumber}`
    }
    return ''
  }

  /** GPS covers both card and non-card processors (e.g. Razorpay, SEPA); only treat it as a card when actual card credentials (number + expiry) are present */
  isCardShapedPayment = (cardInfo = {}) => {
    const { cardNumber = '', expiryMonth = '', expiryYear = '' } = cardInfo || {}
    /** BE sends the literal string 'NA' for non-card processors instead of omitting the fields */
    const isPresent = value => Boolean(value) && value.toString().trim().toUpperCase() !== 'NA'
    return isPresent(cardNumber) && isPresent(expiryMonth) && isPresent(expiryYear)
  }

  getPaymentMethod = () => {
    const { payments = [], otherproperties = {} } = this.props.orderList || {}
    const defaultPaymentType = otherproperties?.defaultPaymentType || ''
    const useCardTypeNode =
      convertToBoolean(APPConfig?.getAppConfig()?.useCardTypeNodeForPayment) || false
    const gpsRedesignFlowEnabled = convertToBoolean(getGPSConfig('enableGPSRedesignFlow'))

    const formatPaymentType = (paymentStr, replaceWith) => {
      if (paymentStr === 'Mercado_Pago') return paymentStr
      return paymentStr
        .replace(/[^a-zA-Z0-9]/g, replaceWith)
        .replace(/\s+/g, replaceWith)
        .trim()
    }

    const resolvePaymentDisplay = (paymentProps, type = '') => {
      if (type?.toLowerCase() === 'storecredit') {
        return i18nTranslate('paymentType.storecredit', type)
      }
      /** API may send otherproperties as an explicit null (not undefined), which skips destructuring defaults */
      const { cardType = '', paymentType = '', displayName = '' } = paymentProps ?? {}

      let raw = useCardTypeNode
        ? (cardType || paymentType)
        : ""

      if (!raw) {
        raw = gpsRedesignFlowEnabled
          ? (displayName || defaultPaymentType || paymentType || type)
          : type
      }
          
      return raw
        ? i18nTranslate(`paymentType.${raw.toLowerCase().replace(/\s+/g, '')}`, raw)
        : ''
    }

    return (
      <>
        {payments?.map((paymentInfo, key) => {
          const { type = '', id = '' } = paymentInfo || {}
          const paymentItemData = paymentInfo?.paymentItems?.[0] ?? {}
          /** otherproperties/cardInfo can be explicit null from the API, which bypasses destructuring defaults */
          const cardInfo = paymentItemData?.cardInfo ?? {}
          const itemProps = paymentItemData?.otherproperties ?? {}
          const isDebitOrCreditCard =
            type?.toLowerCase() === 'creditcard' ||
            type?.toLowerCase() === 'debitcard' ||
           cardInfo && (type?.toLowerCase() === 'gps' && this.isCardShapedPayment(cardInfo))
          if (isDebitOrCreditCard) {
            const cardDetails = this.getCardInfo(cardInfo, itemProps)
            const paymentLabel = resolvePaymentDisplay(itemProps, type)
            return (
              <div key={`cards-${id}`}>
                <span data-testid="qa-order-card-number">{cardDetails}</span>
                {paymentLabel && (
                  <div data-testid={`qa-order-card-payment-${type?.toLowerCase()}`}>
                    {paymentLabel}
                  </div>
                )}
              </div>
            )
          }

          const replaceSpaceWith =
            itemProps?.cardType === 'Chile_WebPay' || itemProps?.paymentType === 'Chile_WebPay'
              ? ''
              : ' '
          /** Non-card GPS payments (Klarna, SEPA, etc.) must prefer displayName over the cardType short code */
          const rawValue = resolvePaymentDisplay(itemProps, type)
          const displayValue = rawValue ? formatPaymentType(rawValue, replaceSpaceWith) : ''
          return (
            <div
              key={`payment-${id}`}
              data-testid={`qa-order-card-payment-${type?.toLowerCase()}`}>
              {displayValue}
            </div>
          )
        })}
      </>
    )
  }
  getOrderSummary = () => {
    const { orderList = {} } = this.props
    const orderItems = orderList.orderInfo?.orderDetails?.orderItems
    const TotalPVCV = orderList.orderInfo?.orderFinancials?.priceFacets || ''
    const totalInclusiveTax = orderList.otherproperties?.totalInclusiveTax || 0

    // IOM-433 - Hide returned items from Order History and Reorder
    const quantity = orderItems.reduce((previousValue, currentItem) => {
      return previousValue + currentItem?.item?.itemInfo?.quantity
    }, 0)
    const priceInfo =
      orderList.orderInfo?.orderFinancials?.orderPriceInfo?.[0] || {}

    const fulfilmentCost = (
      priceInfo.fulfilmentPriceInfo?.fulfilmentSale || 0
    ).toFixed(2)
    const fulfilmentDiscount = (
      priceInfo.fulfilmentPriceInfo?.fulfilmentDiscount || 0
    ).toFixed(2)

    const {
      sale = 0,
      tax = 0,
      discount = 0,
      otherChargesIncludingTax = 0,
      totalSale = 0,
      roundOff = 0,
    } = priceInfo
    /**
     * @note
     * As, Backend support is not there, OrderTotal is calculated manually
     */
    const grandTotal = totalSale + tax + otherChargesIncludingTax + roundOff
    const loyaltyDetails =
      orderList?.orderInfo?.orderDetails?.loyaltyDetails?.[0]?.value || 0

    // Extract store credit from payments
    const { payments = [] } = orderList || {}
    let storeCredit = 0
    if (payments && payments.length > 0) {
      const storeCreditPayment = payments.filter(payment => {
        return payment?.type?.toLowerCase() === 'storecredit'
      })
      // Store credit amount is in paymentItems[0].value
      storeCredit = storeCreditPayment?.[0]?.paymentItems?.[0]?.value || 0
    }

    const summaryValues = {
      priceAfterMarkdown: sale,
      cartDeliveryCharge: fulfilmentCost,
      overAllTax: tax,
      grandTotal: grandTotal,
      count: quantity,
      overAllDiscount: discount,
      cartDeliveryDiscount: fulfilmentDiscount,
      loyaltyDiscount: loyaltyDetails,
      otherChargesIncludingTax: otherChargesIncludingTax,
      totalInclusiveTax: totalInclusiveTax,
      storeCredit: storeCredit,
    }
    const orderPromotions = orderList?.orderInfo?.orderPromotions || []
    /**
     * @todo currencyCode is missing in order history response
     * @see https://jira.skava.net/browse/SECOM-36671
     */

    return (
      <Summary
        math={summaryValues}
        isOrderHistory={true}
        locale={this.props?.locale}
        orderItem={this.props?.orderItem}
        customPriceView={orderList.orderInfo?.orderDetails?.properties || ''}
        showRedeemableSection={false}
        totalValue={TotalPVCV}
        orderPromotions={orderPromotions}
      />
    )
  }
  getOrderDetails = () => {
    const { payments = [] } = this.props.orderList || {}
    const paymentSummary = [
      Array.isArray(payments) && payments.length > 0
        ? {
          label: i18nTranslate(
            'orderSummary.paymentMethodTitle',
            'Payment Method'
          ),
          component: this.getPaymentMethod(),
          className:
            'order-summary-payment-method justify-content-sm-center d-flex px-sm-4',
          dataTestId: 'qa-order-history-payment',
        }
        : '<div class=" col-lg-3 col"></div>',
      {
        label: i18nTranslate('orderSummary.orderSummaryTitle', 'Order Summary'),
        component: this.getOrderSummary(),
        className: 'order-summary-total',
        dataTestId: 'qa-order-summary-container',
      },
    ]

    return {
      paymentSummary,
    }
  }
  checkItemStatus = () => {
    let isCancelAvailable = false
    const walkInCenterChannelTypes = ['WIC', 'PEC']
    const orderItem = this.props?.orderItem || {}
    const orderChannelType =
      orderItem?.orderInfo?.orderDetails?.channel || getWebAvailableChannel()
    const orderList = this.props?.orderList || {}

    // On initial rendering from orderconfirmation page we are getting order list not order item
    const orderInfoStatus = orderList?.orderInfo?.orderDetails?.status?.[0]
    const orderItemStatus = orderItem?.orderInfo?.orderDetails?.status?.[0]
    const orderStatus = orderItemStatus || orderInfoStatus

    if (walkInCenterChannelTypes?.includes(orderChannelType)) {
      isCancelAvailable = false
      return isCancelAvailable
    }
    const orderAllowedPatchStatus =
      this.props.orderList?.allowedPatchFields || []
    orderAllowedPatchStatus.forEach(allowedAction => {
      if (allowedAction.actionName.toLowerCase() === actionState.cancel) {
        isCancelAvailable = true
      }
    })

    if (this.enableLiveEvent) {
      const liveEventCancelableStatuses = [
        'ready_to_edit',
        'submitted_to_fulfillment',
        'received_by_fulfillment',
        'ready_for_pickup',
      ]
      isCancelAvailable = liveEventCancelableStatuses.includes(
        orderStatus?.toLowerCase()
      )
    } else if (orderStatus === 'Ready_To_Edit') {
      isCancelAvailable = true
    } else {
      isCancelAvailable = false
    }
    return isCancelAvailable
  }
  handleSubmit = async (serializedData = {}) => {
    const orderId = this.props.orderList?.orderInfo?.orderDetails?.id
    const orderDetails = {
      orderId,
    }
    const cancellationParams = {
      itemLevelPatchAllowed: false,
      status: 'Canceled',
      otherproperties: {
        reasoncode: serializedData.cancellationReason,
        reasonmessage: serializedData.others,
      },
    }

    const response = await orderHistoryContainer.cancelOrder(
      orderDetails,
      cancellationParams
    )
    if (orderHistoryContainer.isSuccessResponse(response)) {
      this.isCancelClicked = false
      await this.props.getOrderById()
      trackCancelOrderEvent({
        gtmData: this.props?.orderList || {},
        cancel_reason: serializedData.cancellationReason || '',
      })
    }
  }
  renderCancelFormButton = () => {
    return (
      <Col
        lg={3}
        className="form-buttons-group mt-4 d-flex flex-column p-0 py-1">
        <Button
          variant="primary"
          type="submit"
          className={`my-4 w-100 ${this.enableLiveEvent ? 'event-cancel-order-button' : ''
            }`}
          data-testid="qa-cancel-order-button">
          {i18nTranslate('cancelOrderForm.submit', 'Confirm')}
        </Button>
        <Button
          variant="outline-secondary"
          className="w-100 mb-3"
          data-testid="qa-exit-cancel-button"
          onClick={() => {
            this.isCancelClicked = false
          }}>
          {i18nTranslate('cancelOrderForm.cancel', 'Cancel')}
        </Button>
      </Col>
    )
  }

  renderReasonCode = () => {
    const allowedPatchFields = this.props.orderList?.allowedPatchFields || []
    if (allowedPatchFields.length > 0) {
      const options = allowedPatchFields.find(
        name => name.actionName.toLowerCase() === actionState.cancel
      )
      const reasonCode = options.allowedInputs.find(
        code => code.name.toLowerCase() === 'reasoncode'
      )
      return reasonCode
    }
    return []
  }
  renderCancelReason = () => {
    const options = this.renderReasonCode()
    const reasonCode = options?.allowedValues?.map(code => {
      const translatedCode = code.trim().replace(/\s+/g, '_').toLowerCase()
      return i18nTranslate(`reasonCode_${translatedCode}`, code)
    })
    const isRequired = options?.required || ''
    return (
      <Row
        noGutters
        className="order-return-container px-0 py-3 d-flex flex-wrap w-100">
        <Col lg={12} className="mr-auto">
          <CommonForm
            formTypeJson={cancellationFormInput({
              quantityProp: <React.Fragment />,
              reasonCode,
            })}
            schema={schema({ isRequired, isCancel: true })}
            SubmitButtonProps={this.renderCancelFormButton}
            onSubmit={this.handleSubmit}
            hasInlineSubmitButton={false}
            classNameValue={{
              formClassName: 'order-card-return-form d-flex flex-wrap flex-row',
              formRowClassName: 'order-card-form-row px-0 mb-3',
            }}
          />
        </Col>
      </Row>
    )
  }
  handleAfterPrint = componentRef => {
    this.liveEventEnglishPrintTrigger?.focus?.()
    if (this.enableLiveEvent) {
      const orderInfo = componentRef?.props?.item?.[0]?.orderInfo?.find(
        orderId => orderId?.title === 'Order ID:'
      )
      const orderSubtotal =
        componentRef?.props?.item?.[0]?.orderTotalInfo?.find(
          data => data?.label === 'Subtotal'
        )
      const orderShipping =
        componentRef?.props?.item?.[0]?.orderTotalInfo?.find(
          data => data?.label === 'Shipping & Handling:'
        )
      const orderTotal = componentRef?.props?.item?.[0]?.orderTotalInfo?.find(
        data => data?.label === 'Order Total:'
      )
      trackGTMEvents({
        event: 'print_invoice_order_history',
        transactionId: orderInfo?.value || '',
        subtotal: orderSubtotal?.value || '',
        shipping: orderShipping?.value || '',
        total: orderTotal?.value || '',
        total_sv: '',
      })
    }
  }

  renderQRcodeSection = qrData => {
    return (
      <div className="order-summary-qrsection">
        <QRcodeComponent size={119} val={qrData} />
        <div className="order-summary-qrdescription">
          {i18nTranslate(
            'orderSummary.qrSection',
            'Show this QR code at pick-up'
          )}
        </div>
      </div>
    )
  }

  handlePrintOpen = response => {
    if (
      response.labelImageUrl &&
      typeof response.labelImageUrl === 'string' &&
      response.labelImageUrl.endsWith('.pdf')
    ) {
      window.open(
        response.labelImageUrl,
        'ReturnLabelPopup',
        'width=900,height=700,scrollbars=yes,resizable=yes'
      )
    } else {
      toastState.setToastMessage(
        i18nTranslate(
          'orderCard.printReturnLabelError',
          'Unable to open return label. Please try again later.'
        ),
        false
      )
    }
  }

  handleGetReturnLabel = async orderId => {
    const response = await orderHistoryContainer.getReturnLabel(orderId)
    this.handlePrintOpen(response)
  }

  getInvoicesList = () => {
    //Format invoiceDetails: "shortcode1~invoiceid1,shortCode2~invoiceid2"
    const invoiceDetails =
      this.props.orderList?.otherproperties?.invoiceDetails || ''
    if (invoiceDetails === '') return []
    const invoiceIds = invoiceDetails?.toString()?.split(',')
    const uniqueInvoiceIds = [...new Set(invoiceIds)]
    return uniqueInvoiceIds.map((invoiceId, index) => ({
      key: `Invoice ${index + 1}`,
      value: invoiceId?.split('~')[1] || '',
      shortCode: invoiceId?.split('~')[0] || '',
      dataTestId: `qa-invoice-${invoiceId}`,
    }))
  }

  getLiveEventInvoiceOptions = () => {
    return [
      {
        key: '日本語',
        languageCode: 'jp',
        dataTestId: 'qa-invoice-japanese',
      },
      {
        key: 'English',
        languageCode: 'en',
        dataTestId: 'qa-invoice-english',
      },
    ]
  }

  handleLiveEventInvoiceSelect = async languageCode => {
    if (languageCode === 'en') {
      this.liveEventEnglishPrintTrigger?.click()
      return
    }
    const invoicesList = this.getInvoicesList()
    return this.handleInvoiceSelect(
      invoicesList[0]?.value ||
      this.props.orderList?.orderInfo?.orderDetails?.id ||
      '',
      invoicesList[0]?.shortCode || 'JP'
    )
  }

  handleInvoiceSelect = async (invoiceId, shortCode) => {
    overlayState.showLoader()
    const response = await orderHistoryContainer?.getInvoiceOrderUrl(
      invoiceId,
      shortCode
    )
    if (
      orderHistoryContainer?.isSuccessResponse(response) &&
      response.signedUrl
    ) {
      overlayState.hideLoader()
      window.open(response.signedUrl, '_blank')
    } else {
      overlayState.hideLoader()
      toastState.setToastMessage(
        i18nTranslate(
          'orderCard.invoiceOpenError',
          'Unable to open invoice. Please try again later.'
        ),
        false
      )
    }
  }

  getReactToPrint = isHidden => {
    return (
      <ReactToPrint
        trigger={() => (
          <Button
            ref={el => (this.liveEventEnglishPrintTrigger = el)}
            tabIndex={0}
            className={`order-print-button pl-3 pr-3 ${application?.isSmallPhone ? 'printBtn-mobile' : ''
              } ${application?.isTablet ? 'printBtn-tablet' : ''} ${isHidden ? 'd-none' : ''
              }`}
            data-testid="order-card-print-order">
            {i18nTranslate('cart.printInvoice', 'Print Invoice')}
          </Button>
        )}
        content={() => this.componentRef}
        removeAfterPrint={false}
        onAfterPrint={() => this.handleAfterPrint(this.componentRef)}
      />
    )
  }

  renderInvoiceDropdownItems = (liveEventInvoiceOptions, invoicesList) => {
    if (this.enableLiveEvent) {
      return liveEventInvoiceOptions.map(item => {
        return (
          <Dropdown.Item
            as="button"
            onClick={() => this.handleLiveEventInvoiceSelect(item.languageCode)}
            key={item.dataTestId}
            data-testid={item.dataTestId}>
            {item.key}
          </Dropdown.Item>
        )
      })
    }
    if (invoicesList && invoicesList.length > 0) {
      return invoicesList.map((item, index) => {
        return this.renderMenuItem(item, index, this.handleInvoiceSelect)
      })
    }
    return (
      <Dropdown.Item disabled data-testid="qa-no-invoices">
        {i18nTranslate('orderSearch.noInvoices', 'No invoices')}
      </Dropdown.Item>
    )
  }

  renderMenuItem = (item, index, handleOnClick) => {
    const {
      key = '',
      value = '',
      dataTestId = 'qa-dropdown-lists',
      shortCode = '',
    } = item
    return (
      <Dropdown.Item
        as="button"
        onClick={() => this.handleInvoiceSelect(value, shortCode)}
        key={index}
        value={value}
        data-testid={dataTestId}>
        {key}
      </Dropdown.Item>
    )
  }

  renderOrderSummary() {
    /**
     * ! NUSKIN-607
     * ! Hide Cancel Order button unconditionally
     * * const isCancelAvailable = this.checkItemStatus()
     * * const otherUsers = get(this.props, 'otherUsers', false)
     */
    const isCancelAvailable = this.checkItemStatus()
    const otherUsers = this.props?.otherUsers || false

    const { paymentSummary = [] } = this.getOrderDetails()
    const orderList = this.props.orderList || {}
    const item = getPrintOrderDetails(orderList)
    const { enableGiftReceipt = false, customGiftMessage = '' } =
      orderList?.otherproperties || {}
    const orderStatus = orderList.orderInfo?.orderDetails?.orderstatus || ''
    const orderNextStatus =
      orderList.orderInfo?.orderDetails?.nextstatus?.[0] || ''
    const isReturnOrder =
      orderStatus.toLowerCase() === 'return_submitted' &&
        orderNextStatus.toLowerCase() === 'returned'
        ? true
        : false

    const orderId = orderList.orderInfo?.orderDetails?.id || ''
    const isReturnShippingMethodPrepaidReturn =
      orderList.otherproperties?.returnShippingMethod?.toLowerCase() ===
      'prepaidreturn'

    const enableItemLevelInvoices =
      APPConfig.getAppConfig()?.enableItemLevelInvoices === 'true'
    const invoicesList = this.getInvoicesList()
    const isDisableInvoiceInOrderHistory = convertToBoolean(
      APPConfig?.getAppConfig()?.disableInvoiceInOrderHistory
    )
    // change the name and phone nodes
    const recipientName = orderList?.otherproperties?.recipientName || ''
    const recipientPhone = orderList?.otherproperties?.recipientPhone || ''
    let qrData = ''
    if (convertToBoolean(this.enableLiveEvent) && orderId) {
      qrData = `${orderId}`
    } else if (
      isFeatureEnabled('enablePickupRecipientForm') &&
      orderId &&
      recipientName &&
      recipientPhone
    ) {
      qrData = `${orderId} - ${recipientName} - ${recipientPhone}`
    }
    const completedStatusesForEvent = [
      'completed',
      'return_submitted',
      'returned',
    ]
    const isEventOrderCompleted = completedStatusesForEvent.includes(
      orderStatus.toLowerCase()
    )
    const liveEventOrderCompleted =
      this.enableLiveEvent && isEventOrderCompleted
    const isOrderCanceled = ['canceled', 'cancelled'].includes(
      orderStatus.toLowerCase()
    )
    const defaultShowInvoiceDropdown = this.enableLiveEvent
      ? liveEventOrderCompleted
      : enableItemLevelInvoices
    const showInvoiceDropdown = !isOrderCanceled && defaultShowInvoiceDropdown
    const showPrintInvoice =
      !isOrderCanceled &&
      (!isDisableInvoiceInOrderHistory || this.enableLiveEvent)
    const liveEventInvoiceOptions = this.getLiveEventInvoiceOptions()
    let invoiceDropdownWidthClass = 'w-100'
    if (application?.isDesktopOrLarger) {
      invoiceDropdownWidthClass = 'multiple-invoice-dropdown'
    } else if (application?.isTablet) {
      invoiceDropdownWidthClass = 'w-25'
    }
    return (
      <Row noGutters className="w-100 justify-content-end">
        <Col
          lg={9}
          className={`d-flex flex-column flex-sm-row ${this.enableLiveEvent ? 'order-summary-withqr' : ''
            }`}>
          {qrData && this.renderQRcodeSection(qrData)}
          {paymentSummary.map(defaultRenderOrderDetails)}
          {!this.enableLiveEvent &&
            application?.isSmallPhone &&
            displayOrderReturnForSupportedLocales() &&
            isFunction(this.props.renderReturnButton) &&
            this.props.renderReturnButton()}
          {!this.enableLiveEvent &&
            application?.isSmallPhone &&
            enableReorder &&
            isFunction(this.props.renderReorderButton) &&
            this.props.renderReorderButton()}
        </Col>
        <Col
          lg={3}
          className={`pt-3 pt-md-0 d-flex flex-column align-items-center ${application?.isTablet ? 'BtnCol-tablet pt-0' : ''}`}>
          {showInvoiceDropdown ? (
            <div className={`${invoiceDropdownWidthClass}`}>
              <Dropdown className="order-search-box order-status-dropdown w-100">
                <Dropdown.Toggle
                  className="order-search-toggle w-100 d-flex align-items-center justify-content-between"
                  variant=""
                  data-testid="qa-order-status-dropdown">
                  <span
                    className="order-search-dropdown-text text-capitalize text-center flex-grow-1"
                    data-testid="qa-order-selected-status">
                    {i18nTranslate('orderSearch.invoices', 'Invoices')}
                  </span>
                  <span
                    className="order-search-dropdown-arrow justify-content-center align-items-center d-flex"
                    data-testid="qa-order-status-dropdown-arrow">
                    <ArrowDownIcon
                      aria-label={i18nTranslate(
                        'orders.toggle',
                        'Toggle search box'
                      )}
                    />
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="z-1 w-100" dataTestId="qa-dropdown">
                  {this.renderInvoiceDropdownItems(
                    liveEventInvoiceOptions,
                    invoicesList
                  )}
                </Dropdown.Menu>
              </Dropdown>
              {this.getReactToPrint(true)}
            </div>
          ) : (
            showPrintInvoice && <>{this.getReactToPrint(false)}</>
          )}
          <div className="d-none">
            <PrintComponent
              ref={el => (this.componentRef = el)}
              item={item}
              giftMessage={{ enableGiftReceipt, customGiftMessage }}
              // pageStyle={''}
              locale={this.props.locale}
            />
          </div>

          {isReturnOrder && isReturnShippingMethodPrepaidReturn && (
            <Row noGutters className={`${application?.isTablet ? '' : 'pt-3'}`}>
              <Button
                tabIndex={0}
                className={`order-print-button pr-3 ${application?.isSmallPhone ? 'printBtn-mobile' : ''
                  } ${application?.isTablet ? 'printBtn-tablet' : ''}`}
                data-testid="order-card-print-label"
                onClick={() => this.handleGetReturnLabel(orderId)}>
                {i18nTranslate('orderCard.printReturnLabel', 'Return Label')}
              </Button>
            </Row>
          )}
          {/* NUSKIN-607 - START */}
          {/* Hide Cancel Order button unconditionally */}
          {/* {Cancel button will be there when the status will be Ready_To_Edit} */}
          {isCancelAvailable && !otherUsers && (
            <Row noGutters className={`${application?.isTablet ? '' : 'pt-3'}`}>
              <Button
                variant="outline-primary"
                className={`styled-button  ${this.enableLiveEvent ? 'event-cancel-order-button' : ''
                  } ${application?.isTablet ? 'printBtn-tablet' : 'cancel-order-button'}`}
                data-testid="qa-cancel-order"
                onClick={() => {
                  this.isCancelClicked = true
                }}>
                {i18nTranslate('orderCard.cancelOrder', 'Cancel Order')}
              </Button>
            </Row>
          )}
          {/* NUSKIN-607 - END */}
        </Col>
      </Row>
    )
  }
  render() {
    return (
      <Container className="order-summary-container border-top border-bottom mb-3 px-0 py-3 d-flex flex-wrap">
        {!this.isCancelClicked && this.renderOrderSummary()}
        {this.isCancelClicked && this.renderCancelReason()}
      </Container>
    )
  }
}

export default OrderSummary
export { OrderSummary }
